import React, { useCallback } from 'react';

import { Asset } from '@shared/api';

import { useTradeValue } from 'src/lib/trade/hooks/General';

type Props = {
  asset?: Asset;
  value: string;
  blockAlphaNumericChars?: boolean;
  allowNegative?: boolean;
  ignoreAssetScale?: boolean;
  priceScaleOverride?: number;
  maxDecimal?: number;
  onChange: (value: string) => void;
};

// Hook to format and unformat text for input boxes.
// i.e display $100,000 in an input but store as 100000
const useFormatInputText = ({
  asset,
  value,
  blockAlphaNumericChars,
  priceScaleOverride,
  ignoreAssetScale = true,
  allowNegative = false,
  maxDecimal = 16,
  onChange,
}: Props) => {
  const { parseTradeValue, formattedAmount, priceScale } = useTradeValue({
    limitAsset: asset,
    amount: value,
    ignoreAssetScale,
    priceScaleOverride,
  });

  const onChangeValue = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value: newValue, selectionStart } = e.target;

      // If we can only enter whole numbers. Don't process if a decimal is entered
      if (priceScale === 0 && newValue.includes('.')) return;

      let strippedValue = newValue.replace(/^0+(?!\.|$)/, '').replace(/,/g, '');

      // get a snapshot of the current cursor position
      const cursor = selectionStart || 0;
      // determine the offset based on if we have added or removed a character
      const offset = strippedValue.length > (value || '').length ? 1 : 0;
      const decimalOffset = strippedValue === '.' ? 1 : 0;

      // wait 10ms before forcing the location of the cursor
      window.setTimeout(() =>
        e.target.setSelectionRange(cursor + offset + decimalOffset, cursor + offset + decimalOffset),
      );

      if (blockAlphaNumericChars && Number.isNaN(Number(strippedValue))) {
        window.setTimeout(() => e.target.setSelectionRange(cursor - 1, cursor - 1));
        return;
      }

      if (!allowNegative && !Number.isNaN(Number(strippedValue)) && Number(strippedValue) < 0) return;

      // Add a 0 to the front of the value if it starts with a '.'
      if (strippedValue.startsWith('.')) {
        strippedValue = '0' + strippedValue;
      }

      // Check and limit decimal places
      const decimalPosition = strippedValue.indexOf('.');
      if (decimalPosition !== -1) {
        const decimalPart = strippedValue.substring(decimalPosition + 1);
        if (decimalPart.length > maxDecimal) {
          strippedValue = strippedValue.substring(0, decimalPosition + 1 + maxDecimal);
        }
      }

      if (strippedValue.length > 1 && strippedValue.split('').every((v) => v === '0')) return;

      const unformattedValue = strippedValue.replace(/,/g, '');

      const newParsedValue = parseTradeValue(unformattedValue, true, 'none');
      if (newParsedValue !== undefined) onChange(newParsedValue);
    },
    [priceScale, value, blockAlphaNumericChars, allowNegative, parseTradeValue, onChange, maxDecimal],
  );

  return {
    formattedAmount,
    priceScale,
    parseTradeValue,
    onChangeValue,
  };
};

export { useFormatInputText };
